import { addDays, format, startOfDay, startOfWeek } from "date-fns";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import {
  fetchAlertCalendarDay,
  fetchAlertCalendarMonth,
  fetchAlertCalendarWeek,
  fetchSchedulerContentReviewOrganizations,
  type AlertCalendarFilters,
} from "../../../api/frameworksApi";
import { Dropdown } from "../../components/Dropdown";
import { AlertDrawer, type DrawerState } from "./AlertDrawer";
import { CalendarGrid, type CalendarView } from "./CalendarGrid";
import { DayAgenda } from "./DayAgenda";
import {
  DEFAULT_CATEGORY,
  DOCUMENT_TYPE_OPTIONS,
  EMPTY_SUMMARY,
  SUMMARY_TILES,
  blankPayload,
  emptyDay,
  toApiDate,
  type CalendarDay,
  type CalendarEntry,
  type CalendarPayload,
} from "./alertCalendarData";
import { USE_MOCK, mockDay, mockMonth, mockWeek } from "./alertCalendarMock";

const ALL = "all";

const VIEWS: { key: CalendarView; label: string }[] = [
  { key: "day", label: "Day" },
  { key: "week", label: "Week" },
  { key: "month", label: "Month" },
];


/* Just the catch-all option — the filter's name is a label above the control,
   so the trigger and the menu carry nothing but the values themselves. */
function filterOptions(values: { value: string; label: string }[]) {
  return [{ value: ALL, label: "All" }, ...values];
}

function FilterField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1">
      <span className="px-0.5 text-[11px] font-semibold d">{label}</span>
      {children}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   Alert Calendar
   ══════════════════════════════════════════════════════════════════════════ */

export function AlertCalendar() {
  const today = useMemo(() => startOfDay(new Date()), []);

  const [view, setView] = useState<CalendarView>("month");
  const [cursor, setCursor] = useState<Date>(today);
  const category = DEFAULT_CATEGORY;
  const [documentType, setDocumentType] = useState(ALL);
  const [orgId, setOrgId] = useState(ALL);

  const [payload, setPayload] = useState<CalendarPayload | null>(null);
  const [loading, setLoading] = useState(false);
  /* Shares the schedulers page's org endpoint — same tenant, same list. */
  const [orgs, setOrgs] = useState<{ value: string; label: string }[]>([]);
  const [orgsLoading, setOrgsLoading] = useState(true);

  const [drawer, setDrawer] = useState<DrawerState | null>(null);

  const expectedCells = view === "month" ? 42 : view === "week" ? 7 : 1;

  /* ── Organizations ────────────────────────────────────────────────────── */

  useEffect(() => {
    let cancelled = false;

    fetchSchedulerContentReviewOrganizations()
      .then((res) => {
        if (cancelled) return;
        setOrgs(
          (res?.data ?? []).map((o) => ({ value: o.orgId, label: o.orgName })),
        );
      })
      .catch((err: any) => {
        if (cancelled) return;
        const api = err?.response?.data?.errors?.[0];
        console.error(
          "[AlertCalendar] organizations",
          api ? `${api.code} - ${api.detail ?? api.title}` : err,
        );
        setOrgs([]);
      })
      .finally(() => {
        if (!cancelled) setOrgsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  /* ── Fetch ────────────────────────────────────────────────────────────── */

  useEffect(() => {
    let cancelled = false;

    if (USE_MOCK) {
      setPayload(
        view === "month"
          ? mockMonth(cursor.getFullYear(), cursor.getMonth() + 1, today)
          : view === "week"
            ? mockWeek(toApiDate(startOfWeek(cursor)), today)
            : mockDay(toApiDate(cursor), today),
      );
      setLoading(false);
      return;
    }

    setLoading(true);

    const filters: AlertCalendarFilters = {};
    if (category !== ALL) filters.documentCategory = category;
    if (documentType !== ALL) filters.documentType = documentType;
    if (orgId !== ALL) filters.orgId = orgId;

    const request =
      view === "month"
        ? fetchAlertCalendarMonth(cursor.getFullYear(), cursor.getMonth() + 1, filters)
        : view === "week"
          ? fetchAlertCalendarWeek(toApiDate(startOfWeek(cursor)), filters)
          : fetchAlertCalendarDay(toApiDate(cursor), filters);

    request
      .then((res: { data?: CalendarPayload | CalendarDay }) => {
        if (cancelled) return;
        const data = res?.data;
        if (!data) throw new Error("Response carried no data");

        /* The day endpoint returns a bare cell; wrap it so the page has one
           shape to render. It carries no summary, so the tiles keep the last
           set they were given rather than dropping to zero. */
        if ("days" in data) {
          setPayload(data as CalendarPayload);
          return;
        }

        const cell = data as CalendarDay;
        setPayload((prev) => ({
          view: "DAY",
          anchorDate: cell.date,
          rangeStart: cell.date,
          rangeEnd: cell.date,
          summary: prev?.summary ?? EMPTY_SUMMARY,
          days: [cell],
        }));
      })
      .catch((err: any) => {
        if (cancelled) return;
        const api = err?.response?.data?.errors?.[0];
        console.error(
          "[AlertCalendar]",
          api ? `${api.code} - ${api.detail ?? api.title}` : err,
        );
        setPayload(null);
      })
      .finally(() => {
        /* A superseded request leaves the flag alone — the one that replaced
           it is still in flight and owns the loader. */
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [view, cursor, category, documentType, orgId, expectedCells, today]);

  /* A correctly shaped blank grid stands in while loading or on failure, so
     the layout never collapses. */
  const grid = useMemo(
    () =>
      payload ??
      blankPayload(
        view === "month" ? "MONTH" : view === "week" ? "WEEK" : "DAY",
        cursor,
        today,
      ),
    [payload, view, cursor, today],
  );

  const summary = grid.summary;

  /* ── Navigation ───────────────────────────────────────────────────────── */

  const step = (direction: 1 | -1) => {
    setCursor((c) => {
      if (view === "month")
        return new Date(c.getFullYear(), c.getMonth() + direction, 1);
      return addDays(c, view === "week" ? 7 * direction : direction);
    });
  };

  const periodLabel = useMemo(() => {
    if (view === "month") return format(cursor, "MMMM yyyy");
    if (view === "day") return format(cursor, "EEEE, d MMMM yyyy");
    const s = startOfWeek(cursor);
    return `${format(s, "d MMM")} - ${format(addDays(s, 6), "d MMM yyyy")}`;
  }, [view, cursor]);

  const onToday = grid.days.some((d) => d.today);

  /* ── Drawer ───────────────────────────────────────────────────────────── */

  const dayFor = (date: string): CalendarDay =>
    grid.days.find((d) => d.date === date) ??
    emptyDay(new Date(date), today, true);

  const openEntry = (entry: CalendarEntry, date: string, fromList = false) =>
    setDrawer({ kind: "detail", date, entry, fromList });



  return (
    <div className="space-y-3">
      <h1 className="text-2xl font-bold text-foreground">Alert Calendar</h1>

      {/* Toolbar — labelled filters are taller, so everything bottom-aligns */}
      <div className="flex flex-wrap items-end gap-2.5">
        <button
          type="button"
          onClick={() => step(-1)}
          aria-label="Previous period"
          className="flex size-7 items-center justify-center rounded-[7px] border border-border bg-card d transition-colors hover:text-foreground"
        >
          <ChevronLeft className="size-4" />
        </button>
        <span className="min-w-[186px] text-center text-sm font-bold text-foreground">
          {periodLabel}
        </span>
        <button
          type="button"
          onClick={() => step(1)}
          aria-label="Next period"
          className="flex size-7 items-center justify-center rounded-[7px] border border-border bg-card d transition-colors hover:text-foreground"
        >
          <ChevronRight className="size-4" />
        </button>
        <button
          type="button"
          onClick={() => setCursor(today)}
          className={`rounded-lg border px-3 py-1.5 text-[11.5px] font-semibold transition-colors ${onToday
              ? "border-indigo-400/60 bg-indigo-500/10 text-indigo-600 dark:text-indigo-400"
              : "border-border bg-card d hover:text-foreground"
            }`}
        >
          Today
        </button>

        <div className="inline-flex overflow-hidden rounded-lg border border-border">
          {VIEWS.map((v) => (
            <button
              key={v.key}
              type="button"
              onClick={() => setView(v.key)}
              aria-pressed={view === v.key}
              className={`border-r border-border px-3.5 py-1.5 text-[11.5px] font-semibold transition-colors last:border-r-0 ${view === v.key
                  ? "bg-indigo-500/10 text-indigo-600 dark:text-indigo-400"
                  : "bg-card d hover:text-foreground"
                }`}
            >
              {v.label}
            </button>
          ))}
        </div>

        <FilterField label="Organization">
          <Dropdown
            value={orgId}
            onChange={setOrgId}
            options={filterOptions(orgs)}
            loading={orgsLoading}
            searchable={orgs.length > 8}
            size="sm"
          />
        </FilterField>

        {/* Governance is the sole option — no control, nothing else to pick. */}
        <FilterField label="Category">
          <span className="inline-flex h-[38px] min-w-[160px] items-center rounded-lg border border-border bg-card px-3 text-sm text-foreground">
            Governance
          </span>
        </FilterField>

        <FilterField label="Document Type">
          <Dropdown
            value={documentType}
            onChange={setDocumentType}
            options={filterOptions(DOCUMENT_TYPE_OPTIONS)}
            size="sm"
          />
        </FilterField>
      </div>

      {/* Calendar surface */}
      <div className="relative overflow-hidden rounded-xl border border-border bg-card">
        {/* The grid stays put underneath — a filter change dims and locks it
            rather than emptying the page. */}
        {loading && (
          <div
            aria-live="polite"
            className="absolute inset-0 z-10 flex items-center justify-center bg-card/60 backdrop-blur-[1px]"
          >
            <span className="flex items-center gap-2 rounded-full border border-border bg-card px-3.5 py-2 text-[11.5px] font-semibold d shadow-md">
              <Loader2 className="size-3.5 animate-spin" />
              Loading calendar...
            </span>
          </div>
        )}

        {/* Summary tiles — counts documents, not cells */}
        <div className="flex flex-wrap border-b border-border">
          {SUMMARY_TILES.map((tile) => (
            <div
              key={tile.key}
              className="flex min-w-[50%] flex-1 items-center gap-2.5 border-r border-border/60 px-4 py-3 last:border-r-0 sm:min-w-0"
            >
              <span className={`size-2.5 flex-none rounded-full ${tile.dot}`} />
              <span>
                <span
                  className={`block text-[19px] font-bold leading-none tabular-nums ${tile.tone}`}
                >
                  {summary[tile.key]}
                </span>
                <span className="mt-0.5 block text-[10.5px] font-semibold d">
                  {tile.label}
                </span>
              </span>
            </div>
          ))}
        </div>

        {/* Legend — month reads as counts, week and day as cards */}
        <div className="flex flex-wrap items-center gap-4 border-b border-border/60 px-4 py-2.5">
          {view === "month" ? (
            <>
              <span className="flex items-center gap-1.5 text-[11px] font-semibold d">
                <span className="h-3 w-4 flex-none rounded-sm border-l-[3px] border-amber-600 bg-amber-500/[0.14]" />
                Review due - falls due that day
              </span>
              <span className="flex items-center gap-1.5 text-[11px] font-semibold d">
                <span className="size-2.5 flex-none rounded-full bg-violet-600" />
                New alerts - window opens
              </span>
              <span className="flex items-center gap-1.5 text-[11px] font-semibold d">
                <span className="size-2.5 flex-none rounded-full bg-muted-foreground" />
                Reminders - still counting down
              </span>
              {/* <span className="ml-auto text-[11px] d">
                Click a document for detail - open Reminders for the whole day
              </span> */}
            </>
          ) : (
            <>
              <span className="flex items-center gap-1.5 text-[11px] font-semibold d">
                <span className="h-3 w-4 flex-none rounded-sm border border-border border-l-[3px] border-l-amber-600 bg-card" />
                Review due - falls due that day
              </span>
              <span className="flex items-center gap-1.5 text-[11px] font-semibold d">
                <span className="size-2.5 flex-none rounded-full bg-violet-600" />
                New alerts - window opens
              </span>
              <span className="flex items-center gap-1.5 text-[11px] font-semibold d">
                <span className="size-2.5 flex-none rounded-full bg-muted-foreground" />
                Reminders - still counting down
              </span>
              {/* <span className="ml-auto text-[11px] d">
                Click any card for document detail
              </span> */}
            </>
          )}
        </div>

        {view === "day" ? (
          <DayAgenda
            day={grid.days[0]}
            onSelect={(entry, date) => openEntry(entry, date)}
          />
        ) : (
          <CalendarGrid
            view={view}
            days={grid.days}
            onSelect={(entry, date) => openEntry(entry, date)}
            onOpenDay={(date) => setDrawer({ kind: "day", date })}
          />
        )}
      </div>

      <AlertDrawer
        state={drawer}
        day={drawer ? dayFor(drawer.date) : null}
        onOpenChange={(open) => !open && setDrawer(null)}
        onOpenEntry={(entry) => drawer && openEntry(entry, drawer.date, true)}
        onBackToDay={(date) => setDrawer({ kind: "day", date })}
      />
    </div>
  );
}
